// Video KYC API — /onboarding/applications/{id}/video-kyc* (public).
// The state machine lives on the server: NOT_STARTED → (join) IN_PROGRESS →
// (complete) DONE_AUDIT_PENDING → bank review APPROVED / REJECTED; also
// SCHEDULED and FAILED. Links expire after 3 days; 3 attempts max.
import { get, isRealApiEnabled, post } from './client';
import { getMockOnboardingApplication, updateMockOnboardingApplication } from './onboarding';

export type VideoKycStateName =
  | 'NOT_STARTED'
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'DONE_AUDIT_PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'FAILED';

export type VideoKycOutcome =
  | 'SUCCESS'
  | 'POOR_CONNECTION'
  | 'PAN_UNCLEAR'
  | 'FACE_MISMATCH'
  | 'WRONG_ANSWER';

export interface VideoKycSlot {
  startsAt: string;
  endsAt: string;
  available: boolean;
  placesLeft: number;
}

export interface VideoKycStatus {
  state: VideoKycStateName;
  attempts: number;
  joinLink?: string | null;
  linkIssuedAt?: string | null;
  linkExpiresAt?: string | null;
  scheduledAt?: string | null;
  lastOutcome?: string | null;
  rejectionReason?: string | null;
  /** Security question the officer asks during the call. */
  question?: string | null;
  linkExpired?: boolean;
}

const base = (applicationId: string) =>
  `/onboarding/applications/${encodeURIComponent(applicationId)}/video-kyc`;

export const getVideoKyc = (applicationId: string): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) {
    const application = getMockOnboardingApplication();
    if (!application || application.applicationId !== applicationId) throw new Error('Mock application not found.');
    return Promise.resolve({ state: application.status === 'KYC_COMPLETED' ? 'DONE_AUDIT_PENDING' : 'NOT_STARTED', attempts: 0, joinLink: null, linkIssuedAt: null, linkExpiresAt: null, scheduledAt: null, lastOutcome: null, rejectionReason: null, question: 'What is your date of birth?', linkExpired: false });
  }
  return get<VideoKycStatus>(base(applicationId));
};

export const joinVideoKyc = (applicationId: string): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) return Promise.resolve({ state: 'IN_PROGRESS', attempts: 1, joinLink: 'https://mock.local/video-kyc', linkIssuedAt: new Date().toISOString(), linkExpiresAt: new Date(Date.now() + 3 * 86400000).toISOString(), question: 'What is your date of birth?', linkExpired: false });
  return post<VideoKycStatus>(`${base(applicationId)}/join`);
};

export const scheduleVideoKyc = (
  applicationId: string,
  slot: string
): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) return Promise.resolve({ state: 'SCHEDULED', attempts: 0, scheduledAt: slot, question: 'What is your date of birth?' });
  return post<VideoKycStatus>(`${base(applicationId)}/schedule`, undefined, { params: { slot } });
};

export const leaveVideoKyc = (applicationId: string): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) return Promise.resolve({ state: 'NOT_STARTED', attempts: 0 });
  return post<VideoKycStatus>(`${base(applicationId)}/leave-queue`);
};

export const completeVideoKyc = (
  applicationId: string,
  outcome?: VideoKycOutcome,
): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) {
    const result = outcome ?? 'SUCCESS';
    if (result === 'SUCCESS') updateMockOnboardingApplication({ status: 'KYC_COMPLETED', currentStep: 9 });
    return Promise.resolve({ state: result === 'SUCCESS' ? 'DONE_AUDIT_PENDING' : 'REJECTED', attempts: 1, lastOutcome: result, rejectionReason: result === 'SUCCESS' ? null : result, question: 'What is your date of birth?' });
  }
  return post<VideoKycStatus>(`${base(applicationId)}/complete`, undefined, { params: outcome ? { outcome } : undefined });
};

export const getVideoKycSlots = (
  applicationId: string,
  date?: string,
): Promise<VideoKycSlot[]> => {
  if (!isRealApiEnabled()) return Promise.resolve([{ startsAt: `${date ?? new Date().toISOString().slice(0, 10)}T10:00:00Z`, endsAt: `${date ?? new Date().toISOString().slice(0, 10)}T10:30:00Z`, available: true, placesLeft: 10 }]);
  return get<VideoKycSlot[]>(`${base(applicationId)}/slots`, { params: date ? { date } : undefined });
};

export const restartVideoKyc = (applicationId: string): Promise<VideoKycStatus> => {
  if (!isRealApiEnabled()) { updateMockOnboardingApplication({ status: 'SUBMITTED' }); return Promise.resolve({ state: 'NOT_STARTED', attempts: 0 }); }
  return post<VideoKycStatus>(`${base(applicationId)}/restart`);
};

// §18.3 customer-facing outcome messages.
export const OUTCOME_MESSAGES: Record<string, string> = {
  SUCCESS: 'Your Video KYC is complete.',
  POOR_CONNECTION: 'The connection dropped during the call. Please find a stable network and try again.',
  PAN_UNCLEAR: 'We could not read your PAN card clearly. Hold it steady in good light and try again.',
  FACE_MISMATCH: 'We could not match your face to your documents. Remove glasses or headwear and try again.',
  WRONG_ANSWER: 'One of your answers did not match our records. Please try again.',
};
